import Send from './send';
import SendSlideout from './SendSlideout';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDocumentState } from '../../../../../../contexts/DocumentContext';
import { useApolloClient, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { DocumentStatusEnum } from '../../../../../../shared/constants/document';
import { useDocumentTeamPermissions } from '../../../../../../pages/document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import { useDeal } from '../../../../../../contexts/DealContext';
import { sendDocumentToReceiver } from '../../../../../../utils/api/send-document-to-receiver';
import { givePossessionToCounterparty } from '../../../../../../utils/api/give-possession-to-counterparty';
import { saveDocumentVersionOnPossessionPass } from '../../../document-wizard-header.utils';
import {
  DocumentSourceEnum,
  givePossessionSuccessMessage,
  ShareWithUnresolvedSuggestionsWarning,
  successSendDocumentMessage,
} from '../../../../document-editor/constants/EditorConstats';
import { useDocumentRole } from '../../../../../../shared/hooks/useDocumentRole/useDocumentRole';
import { useNotifications } from '../../../../../../notifications';
import { paths } from '../../../../../../routing';
import { Button, Dropdown } from 'react-bootstrap';
import { permissionLevelToBooleans } from '../../../../../../types/counterparty-permissions';
import { CounterpartyPermissionLevel } from '../../../../../../types/counterparty-permissions';
import { useModals } from '../../../../../../contexts/ModalsContext';
import PlaceholderWarningModal from '../../../../document-wizard-modal/custom-modals/placeholderWarningModal/placeholderWarningModal';
import { mutations } from '../../../../../../api';
import { usePendingCounterpartySuggestions } from '../usePendingCounterpartySuggestions';
import { flushActiveEditor } from '../../../../document-editor/flushActiveEditor';

interface Props {
  teamId: string;
  documentId: string;
  dealId: string;
  handleSaveDocument: (() => Promise<void>) | undefined;
  isDropdownOption?: boolean;
  showCounterparty?: boolean;
  counterpartyEmail?: string;
  counterpartyName?: string;
  placeholdersToComplete?: number;
  onActionTriggered?: (action: () => void) => void;
  isOpen?: boolean; // External control from Redux
  onClose?: () => void; // External close handler
  onModalOpen?: () => void; // Callback when modal opens (to close parent dropdown)
  onParentModalClose?: () => void; // Callback to close parent "Before You Go" modal
  dropdownLabel?: string; // Custom label for dropdown option
  isUserDealCreator?: boolean; // Whether user is on the original deal creator team
}

const SendContainer = ({
  handleSaveDocument,
  documentId,
  dealId,
  teamId,
  isDropdownOption,
  showCounterparty,
  counterpartyEmail,
  counterpartyName,
  placeholdersToComplete = 0,
  onActionTriggered: _onActionTriggered,
  isOpen: externalIsOpen,
  onClose: externalOnClose,
  onModalOpen,
  onParentModalClose,
  dropdownLabel,
  isUserDealCreator,
}: Props) => {
  const { state, updateDocumentState } = useDocumentState();
  const { showSendDocumentModal: isSendDocumentModalOpenFromRedux, setShowSendDocumentModal } = useModals();
  const navigate = useNavigate();
  const [, { error: notifyError, success: notifySuccess }] = useNotifications();

  const sessionActivities = state.sessionActivities;
  // Read permission level directly from context (single source of truth)
  const counterpartyPermissionLevel = state.counterpartyPermissionLevel;
  const currentDocumentId = state.id;

  const _hasRequiredPlaceholders = placeholdersToComplete !== 0;

  const [showWarningModal, setShowWarningModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);

  // Mutation for immediate permission level updates (sync across all UI)
  const [setPermissionLevel] = useMutation(mutations.setCounterpartyPermissionLevel, {
    onCompleted: (data) => {
      const newLevel = data.setCounterpartyPermissionLevel.counterpartyPermissionLevel as CounterpartyPermissionLevel;
      updateDocumentState({ counterpartyPermissionLevel: newLevel, ...permissionLevelToBooleans(newLevel) });
      // Emit custom event for WebViewer DOM dropdown sync
      window.dispatchEvent(new CustomEvent('propdocs:permissionChanged', {
        detail: { type: 'level', value: newLevel }
      }));
    },
  });

  // Polymorphic version snapshot — the docx-native equivalent of CKEditor's
  // RevisionTracker.saveRevision(). See handleSendDocumentMutation.
  const [saveVersionMutation] = useMutation(mutations.saveDocumentVersion);

  // Handler for permission level changes
  const handleCounterpartyPermissionChange = useCallback((level: CounterpartyPermissionLevel) => {
    const docId = documentId || currentDocumentId;
    if (docId) {
      setPermissionLevel({ variables: { documentId: docId, permissionLevel: level } });
    }
  }, [documentId, currentDocumentId, setPermissionLevel]);

  const documentStatus = state.status;
  const optionalMessageInputRef = React.useRef<HTMLTextAreaElement>(null);
  const documentSource = state.source;
  const isDocumentEditorLoadedState = state.isEditorLoaded;
  const { formatMessage } = useIntl();
  const apolloClient = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);
  const { hasInviteSendingPermission, hasEditPermission } = useDocumentTeamPermissions({ teamId, apolloClient });
  const { currentDeal } = useDeal();
  const receiverTeamId = state.receiverTeamId;
  // Check both deal receiver email and document receiver team to determine if deal is shared
  const isDealShared = !!(currentDeal?.receiverEmail || receiverTeamId);
  // Check if we have a counterparty - either with full details or just a receiver team
  const hasCounterparty = !!(receiverTeamId || (showCounterparty && (counterpartyEmail || counterpartyName)));
  const isDocumentInDraft = [DocumentStatusEnum.DRAFT, DocumentStatusEnum.UPLOADED, DocumentStatusEnum.SHARED].includes(
    documentStatus as DocumentStatusEnum
  );
  const { possessionTeamName, userRole, isDocumentCreator } = useDocumentRole(documentId);
  const pendingCounterpartySuggestions = usePendingCounterpartySuggestions(documentId, userRole);

  const dealUrl = paths.deals.detail.replace(':teamId', teamId as string).replace(':dealId', dealId);


  const canShare = useMemo(() => {
    if (!documentStatus) return true;

    const isShareableStatus = [
      DocumentStatusEnum.DRAFT,
      DocumentStatusEnum.REVIEWING,
      DocumentStatusEnum.UPLOADED,
      DocumentStatusEnum.SHARED,
    ].includes(documentStatus as DocumentStatusEnum);

    return (hasInviteSendingPermission || hasEditPermission) && isShareableStatus;
  }, [hasInviteSendingPermission, hasEditPermission, documentStatus]);

  const isEditorReadyForSend = documentSource === DocumentSourceEnum.APP ? isDocumentEditorLoadedState : true;
  const canShareNow = canShare && isEditorReadyForSend;

  const sentForExternalReview = useMemo(() => {
    return [DocumentStatusEnum.DRAFT].includes(documentStatus as DocumentStatusEnum);
  }, [documentStatus]);

  const hasProcessedExternalOpenRef = React.useRef(false);

  // Sync external or Redux state with modal visibility
  useEffect(() => {
    // If externally controlled (from RequestReviewContainer), use that
    if (externalIsOpen !== undefined) {
      // Only process when transitioning to open, not on every prop change
      if (externalIsOpen && !hasProcessedExternalOpenRef.current) {
        hasProcessedExternalOpenRef.current = true;
        if (placeholdersToComplete > 0) {
          setShowWarningModal(true);
        } else {
          setShowSendModal(true);
        }
      } else if (!externalIsOpen) {
        // Reset when closing
        hasProcessedExternalOpenRef.current = false;
        setShowSendModal(false);
        setShowWarningModal(false);
      }
    }
    // Otherwise use Redux state for navigation from document table (legacy)
    else if (isSendDocumentModalOpenFromRedux && !showSendModal) {
      setShowSendModal(true);
    }
  }, [externalIsOpen, isSendDocumentModalOpenFromRedux, showSendModal, placeholdersToComplete]);

  const handleSendDocumentMutation = useCallback(async () => {
    const isAppDocument = documentSource === DocumentSourceEnum.APP;

    if (isAppDocument) {
      handleSaveDocument && (await handleSaveDocument());

      // Land edits still inside the autosave debounce and reconcile the
      // relational suggestion rows before the server reads them.
      await flushActiveEditor(documentId);

      // Snapshot the body as it is handed over. This version is what the party
      // WITHOUT possession is shown while the other side holds the document, so
      // it has to exist for every engine — the old code only ran for CKEditor's
      // RevisionTracker plugin, so docx-native deals silently created none and
      // the counterparty had nothing to fall back to.
      const counterpartyNameForRevision = counterpartyName || 'counterparty';
      const revisionName = `Sent to ${counterpartyNameForRevision}`;

      try {
        await saveVersionMutation({
          variables: {
            documentId: String(documentId),
            documentType: 'Document',
            name: revisionName,
            event: 'possession_transfer',
          },
        });
      } catch (error) {
        console.error('Failed to create send-to-counterparty revision:', error);
        // Don't block the send operation if revision creation fails
      }
    }

    const additionalMessage = optionalMessageInputRef.current?.value;

    setTimeout(async () => {
      // Call the appropriate mutation based on document status with correct parameters
      // Use Redux values (single source of truth, already persisted via immediate mutations)
      const mutationPromise = isDocumentInDraft
        ? sendDocumentToReceiver(documentId, additionalMessage, counterpartyPermissionLevel)(apolloClient)
        : givePossessionToCounterparty({
            documentId,
            additionalMessage,
            sessionActivities,
            counterpartyPermissionLevel,
          })(apolloClient);

      await mutationPromise
        .then(() => {
          updateDocumentState({ sessionActivities: [] });
          // Navigate first, then show success notification with delay
          navigate(dealUrl);

          // Show success notification after navigation completes
          setTimeout(() => {
            notifySuccess({
              message: formatMessage(
                documentStatus === DocumentStatusEnum.DRAFT ? successSendDocumentMessage : givePossessionSuccessMessage
              ),
            });
          }, 100);
        })
        .catch((error) => {
          if (isAppDocument) {
            saveDocumentVersionOnPossessionPass({ success: false });
          }

          notifyError({ message: error.message });
        });
    }, 2000);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    documentSource,
    isDocumentInDraft,
    documentId,
    sessionActivities,
    counterpartyPermissionLevel,
    apolloClient,
    handleSaveDocument,
    sentForExternalReview,
    possessionTeamName,
    updateDocumentState,
    navigate,
    dealUrl,
    isDealShared,
    notifySuccess,
    formatMessage,
    documentStatus,
    notifyError,
    placeholdersToComplete,
    counterpartyName,
    saveVersionMutation,
  ]);

  const handleSendModalHide = () => {
    setShowSendModal(false);
    setShowWarningModal(false);

    // Use external close handler if provided, otherwise reset Redux state
    if (externalOnClose) {
      externalOnClose();
    } else {
      setShowSendDocumentModal(false); // Reset Redux state
    }
  };

  const handleSendDocument = useCallback(async () => {
    setIsLoading(true);

    if (documentSource === DocumentSourceEnum.IMPORTED) {
      return handleSendDocumentMutation();
    }

    return handleSendDocumentMutation();
  }, [documentSource, handleSendDocumentMutation]);

  const getPendingSuggestionsAlert = () => {
    // From the relational suggestion rows, NOT CKEditor's TrackChanges plugin —
    // that read returned undefined under the DOCX-native editor, so this warning
    // silently stopped appearing. See usePendingCounterpartySuggestions.
    const suggestionsNumber = pendingCounterpartySuggestions.fromCounterparty;

    if (!suggestionsNumber) return null;
    return {
      text: ShareWithUnresolvedSuggestionsWarning.text.replace('{num}', `${suggestionsNumber}`),
      title: ShareWithUnresolvedSuggestionsWarning.title,
    };
  };

  return (
    <>
      {/* Only render button if not being used purely for modal management */}
      {externalIsOpen === undefined && !isDropdownOption && <Send disabled={!canShareNow} onClick={() => {
        onModalOpen?.(); // Close parent dropdown
        setShowSendModal(true);
      }} />}
      {externalIsOpen === undefined && isDropdownOption && (
        <Dropdown.Item
          as={Button}
          variant="link"
          onClick={() => {
            onModalOpen?.(); // Close parent dropdown
            onParentModalClose?.(); // Close parent "Before You Go" modal
            if (placeholdersToComplete === 0) {
              setShowWarningModal(false);
              setShowSendModal(true);
            } else {
              setShowWarningModal(true);
              setShowSendModal(false);
            }
          }}
          disabled={!canShareNow}
        >
          {dropdownLabel || 'via Propdocs'}
        </Dropdown.Item>
      )}
      <PlaceholderWarningModal
        isOpen={showWarningModal}
        placeholders={placeholdersToComplete}
        onContinue={() => {
          setShowWarningModal(false);
          // Delay slideout opening to ensure modal closes first
          setTimeout(() => {
            setShowSendModal(true);
          }, 100);
        }}
        onHide={() => {
          setShowWarningModal(false);
          if (externalOnClose) externalOnClose();
        }}
        onReview={() => {
          setShowWarningModal(false);
          if (externalOnClose) externalOnClose();
        }}
      />

      <SendSlideout
        isOpen={showSendModal}
        onClose={handleSendModalHide}
        isDealShared={isDealShared}
        handler={handleSendDocument}
        isLoading={isLoading}
        suggestionsAlert={getPendingSuggestionsAlert?.()}
        showCounterparty={showCounterparty}
        counterpartyEmail={counterpartyEmail}
        counterpartyName={counterpartyName}
        optionalMessageInputRef={optionalMessageInputRef}
        teamId={teamId}
        hasCounterparty={hasCounterparty}
        counterpartyPermissionLevel={counterpartyPermissionLevel}
        onCounterpartyPermissionChange={handleCounterpartyPermissionChange}
        documentSource={documentSource}
        documentStatus={documentStatus}
        isUserDealCreator={isUserDealCreator}
        isDocumentCreator={isDocumentCreator}
      />
    </>
  );
};

export default SendContainer;
